import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Radio, Users, CheckCircle2, Clock, WifiOff } from 'lucide-react';
import { liveApi, LiveStatusData, StageStatus } from '@/api/endpoints/live';
import { ExportReportsPanel } from '@/components/reports/ExportReportsPanel';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { Countdown } from '@/components/competition/Countdown';

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Returns "Xh YYmin", "Y min" or "Zs" from a millisecond duration. */
function formatMinutesLeft(ms: number): string {
  if (ms <= 0) return '0s';
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}min`;
  if (m > 0) return `${m} min`;
  return `${s}s`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatBlock({
  icon: Icon,
  value,
  label,
  color,
}: {
  icon: React.ElementType;
  value: number;
  label: string;
  color: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
      <Icon className={`mb-2 h-8 w-8 ${color}`} />
      <p className={`text-5xl font-bold tabular-nums ${color}`}>{value}</p>
      <p className="mt-1 text-sm font-medium text-gray-500">{label}</p>
    </div>
  );
}

/**
 * Shows inProgress/completed counts for one active stage.
 * "Pozostało" is ticked locally every 60 s using the stable server offset —
 * independent of React Query polling.
 */
function LiveStageView({
  stage,
  serverOffset,
}: {
  stage: StageStatus;
  serverOffset: number;
}) {
  const [remainingMs, setRemainingMs] = useState<number | null>(() => {
    if (!stage.endTime) return null;
    return Math.max(0, new Date(stage.endTime).getTime() - (Date.now() + serverOffset));
  });

  const timeLeftStr = remainingMs !== null ? formatMinutesLeft(remainingMs) : null;

  useEffect(() => {
    if (!stage.endTime) return;

    const update = () => {
      const ms = Math.max(0, new Date(stage.endTime!).getTime() - (Date.now() + serverOffset));
      setRemainingMs(ms);
    };

    update();
    const tickMs = remainingMs !== null && remainingMs < 60_000 ? 1_000 : 60_000;
    const id = setInterval(update, tickMs);
    return () => clearInterval(id);
  }, [stage.endTime, serverOffset, remainingMs !== null && remainingMs < 60_000]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-3 py-1 text-sm font-semibold text-green-700">
          <span className="h-2 w-2 animate-pulse rounded-full bg-green-500" />
          {stage.label} — w trakcie
        </span>
        {timeLeftStr && (
          <span className="flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-sm font-mono font-semibold text-gray-600">
            <Clock className="h-3.5 w-3.5" />
            Pozostało: {timeLeftStr}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <StatBlock
          icon={Users}
          value={stage.inProgressCount}
          label="Pisze test"
          color="text-blue-600"
        />
        <StatBlock
          icon={CheckCircle2}
          value={stage.completedCount}
          label="Zakończyło"
          color="text-green-600"
        />
      </div>
    </div>
  );
}

// ─── Views ────────────────────────────────────────────────────────────────────

function IdleView() {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      <WifiOff className="h-12 w-12 text-gray-300" />
      <p className="text-lg font-semibold text-gray-400">Brak testu w trakcie</p>
      <p className="text-sm text-gray-400">
        Żaden etap nie jest aktualnie aktywny ani zaplanowany.
      </p>
    </div>
  );
}

/**
 * Countdown view for a scheduled stage.
 *
 * `serverOffset` is computed once when the page loads and passed down as a
 * stable value — the Countdown component runs its own setInterval and never
 * needs to hit the network again just to tick the clock.
 */
function CountdownView({
  data,
  serverOffset,
}: {
  data: LiveStatusData;
  serverOffset: number;
}) {
  const startMs = data.countdownTarget
    ? new Date(data.countdownTarget).getTime()
    : Date.now();

  const countdownSlot = data.stages.find(
    (s) => s.stage === data.countdownStage && s.term === (data.countdownTerm ?? 1),
  );
  const countdownLabel = countdownSlot?.label ?? `Etap ${data.countdownStage}`;

  return (
    <div className="flex flex-col items-center gap-4 py-8">
      <span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-blue-700">
        {countdownLabel}
      </span>
      <Countdown
        startTime={startMs}
        serverTimeOffset={serverOffset}
        onComplete={() => {}}
        title={`${countdownLabel} rozpocznie się za`}
        precision="minutes"
      />
    </div>
  );
}

function LiveView({
  data,
  serverOffset,
}: {
  data: LiveStatusData;
  serverOffset: number;
}) {
  const activeStages = data.stages.filter((s) => s.phase === 'active');
  return (
    <div className="space-y-6">
      {activeStages.map((s) => (
        <LiveStageView key={`${s.stage}-${s.term}`} stage={s} serverOffset={serverOffset} />
      ))}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export const LivePage = ({ readOnly = false }: { readOnly?: boolean }) => {
  /**
   * Computed once from the first successful response and never updated.
   * All child components use this stable offset to drive local timers —
   * subsequent React Query polls only refresh phase/count data, not the clock.
   */
  const serverOffsetRef = useRef<number | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['live-status'],
    queryFn: liveApi.getLiveStatus,
    // Poll every 5 s during live (to update in-progress/completed counts),
    // 30 s otherwise (just to detect phase transitions).
    refetchInterval: (query) => {
      const view = query.state.data?.view;
      return view === 'live' ? 5_000 : 30_000;
    },
    staleTime: 0,
  });

  // Capture server offset from the very first response only.
  useEffect(() => {
    if (data && serverOffsetRef.current === null) {
      serverOffsetRef.current = Date.now() - new Date(data.serverTime).getTime();
    }
  }, [data]);

  const serverOffset = serverOffsetRef.current ?? 0;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
          <Radio className="h-6 w-6 text-primary-600" />
          Na żywo
        </h2>
        <p className="mt-1 text-sm text-gray-500">
          {readOnly
            ? 'Test teoretyczny — Etap 2 · odświeżane automatycznie'
            : 'Aktualny status etapów turnieju — odświeżane automatycznie'}
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Status turnieju</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading && <LoadingSpinner size="md" className="py-10" />}

          {isError && (
            <p className="py-10 text-center text-sm text-red-500">
              Błąd pobierania statusu. Spróbuj odświeżyć stronę.
            </p>
          )}

          {data && data.view === 'idle' && <IdleView />}
          {data && data.view === 'countdown' && (
            <CountdownView data={data} serverOffset={serverOffset} />
          )}
          {data && data.view === 'live' && (
            <LiveView data={data} serverOffset={serverOffset} />
          )}
        </CardContent>
      </Card>

      {!readOnly && <ExportReportsPanel readOnly={readOnly} />}
    </div>
  );
};
